import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import ReCAPTCHA from 'react-google-recaptcha'
import { useNavigate } from 'react-router-dom'

import Card from '@components/card'
import Title from '@components/text/title'
import SubText from '@components/text/subText'
import TextInput from '@components/inputs/textInput'
import Loading from '@components/loading'
import PrimaryButton from '@components/inputs/button/primaryButton'
import Footer from '@components/footer'
import Switch from '@components/inputs/switch'
import InsurerLogo from '@components/insurerLogo'
import showToast from '@util/ToastUtil'

import colors from '@settings/styleSettings'

import backgroundImage from '@assets/images/login-background.jpg'
import { getReport as getReportAction } from '@actions/beneficiary'

import { isNormalFormatValidDate } from '@util/timeUtil'
import { cpfShowMask, dateMask } from '@util/stringUtil'
import { Container, GreenContainer } from '../styles'

import {
  LogoContainer,
  TextContainer,
  CardContainer,
  RecaptchaContainer,
  FormContainer,
  SwitchArea,
  FooterContainer,
} from './styles'

const BeneficiaryLogin = ({ getReport, reportData }) => {
  const [cpf, setCpf] = useState('')
  const [birthdate, setBirthdate] = useState('')
  const [loading, setLoading] = useState(false)
  const [captcha, setCaptcha] = useState(false)
  const [rememberMe, setRememberMe] = useState(!!localStorage.getItem('user'))

  const recaptchaSiteKey = (
    process.env.REACT_APP_RECAPTCHA_SITE_KEY
    || process.env.REACT_APP_RECAPTCHA
  )
  const showRecaptcha = Boolean(recaptchaSiteKey)
  const showMaintenance = false

  const navigate = useNavigate()

  useEffect(() => {
    const beneficiary = JSON.parse(localStorage.getItem('user'))
    if (beneficiary) {
      setCpf(beneficiary.cpf)
      setBirthdate(beneficiary.birthdate)
    }
  }, [])

  useEffect(() => {
    if (reportData) {
      const { id: beneficiaryId } = reportData

      setLoading(false)
      navigate(`/unimed-assis/beneficiario/${beneficiaryId}`)
    }
  }, [reportData])

  const onChangeRememberMe = () => setRememberMe(!rememberMe)

  const callback = async () => {
    rememberMe
      ? localStorage.setItem(
        'user',
        JSON.stringify({ cpf, birthdate })
      )
      : localStorage.removeItem('user')
    setLoading(false)
  }

  const validAttempt = () => {
    if (
      cpf.length === 0
      || birthdate.length === 0
      || cpf.length < 14
      || birthdate.length < 10
    ) {
      showToast('error', 'É necessário preencher completamente os campos de CPF, data de nascimento e carteira Unimed')
      return false
    }

    if (!isNormalFormatValidDate(birthdate)) {
      showToast('error', 'Data inválida')
      return false
    }

    if (showRecaptcha && !captcha) {
      showToast('error', 'É necessário fazer o captcha')
      return false
    }

    return true
  }

  const onSubmit = () => {
    if (validAttempt()) {
      setLoading(true)
      getReport({ cpf, birthdate, captcha }, callback)
    }
  }

  const onChangeRecaptcha = (value) => setCaptcha(value)

  const onCpfChange = (e) => setCpf(cpfShowMask(e.target.value))

  const onBirthDateChange = (e) => setBirthdate(dateMask(e.target.value))

  return (
    <Container
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      <GreenContainer>
        <LogoContainer>
          <InsurerLogo />
        </LogoContainer>
        {showMaintenance ? (
          <Card style={{ maxHeight: 170 }}>
            <CardContainer>
              <TextContainer>
                <Title>Manutenção!</Title>
                <SubText style={{ textAlign: 'center' }}>
                  Estamos passando por uma rápida manutenção para melhorar sua experiência.
                  Em breve, tudo estará funcionando novamente!
                </SubText>
              </TextContainer>
            </CardContainer>
          </Card>
        ) : (
          <Card style={{ maxHeight: 460 }}>
            <CardContainer>
              <TextContainer>
                <Title>Saúde Preventiva</Title>
                <SubText style={{ textAlign: 'center' }}>
                  Insira o seu CPF e a sua data de nascimento para acessar o sistema
                </SubText>
              </TextContainer>
              <FormContainer>
                <TextInput
                  placeholder="CPF"
                  value={cpf}
                  onChange={(e) => onCpfChange(e)}
                />
                <TextInput
                  placeholder="Data de nascimento"
                  value={dateMask(birthdate)}
                  onChange={(e) => onBirthDateChange(e)}
                />
                <RecaptchaContainer>
                  {showRecaptcha && (
                    <ReCAPTCHA
                      sitekey={recaptchaSiteKey}
                      onChange={onChangeRecaptcha}
                    />
                  )}
                </RecaptchaContainer>
                <SwitchArea>
                  <Switch checked={rememberMe} onChange={onChangeRememberMe} />
                  <SubText
                    style={{ color: colors.titleStrongBlack, fontSize: 13 }}
                  >
                    Lembrar de mim
                  </SubText>
                </SwitchArea>
              </FormContainer>
              <PrimaryButton onClick={onSubmit} disabled={loading}>
                {loading ? <Loading /> : 'Acessar'}
              </PrimaryButton>
            </CardContainer>
          </Card>
        )}
        <FooterContainer>
          <Footer />
        </FooterContainer>
      </GreenContainer>
    </Container>
  )
}

BeneficiaryLogin.propTypes = {
  getReport: PropTypes.func.isRequired,
  reportData: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number
  }),
}

BeneficiaryLogin.defaultProps = {
  reportData: null,
}

const mapStateToProps = ({ beneficiary }) => ({
  reportData: beneficiary.reportData,
})

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    getReport: getReportAction,
  },
  dispatch
)

export default connect(mapStateToProps, mapDispatchToProps)(BeneficiaryLogin)
